// src/views/ConfigPublicacoes/ConfigPubForm.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import Select from 'react-select';

import {
  createConfigPublicacao,
  updateConfigPublicacao,
  getConfigPublicacao,
  ConfigPublicacao,
  ConfigPublicacaoFormData,
} from '../../services/configPublicacoesService';
import { getAllPublicEntitiesSimple } from '../../services/publicEntityService';

import { PublicEntitySimpleResponse } from '../../types/publicEntity';

import useAuth from '../../hooks/useAuth';
import EmblemUploadDialog from './EmblemUploadDialog';
import { AddPhotoAlternate } from '@mui/icons-material';

const ConfigPubForm: React.FC = () => {
  const { cfgId } = useParams<{ cfgId: string }>();
  const isEditMode = Boolean(cfgId);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);

  // Campos do formulário (em state)
  const [formData, setFormData] = useState<ConfigPublicacaoFormData>({
    entity_id: '', // será selecionado via react-select
    prefix: '',
    suffix: '',
    type: '',
    manager_name: '',
    editor_name: '',
    address_street: '',
    address_cep: '',
    year_roman_start_from: 1,
    edition_number_start_from: 1,
    header_title: '',
    header_subtitle: '',
    dom_enabled: true,
    dou_enabled: false,
    jgc_enabled: false,
    emblem_file: undefined,
  });

  // Estado para a lista de entidades públicas
  const [publicEntities, setPublicEntities] = useState<PublicEntitySimpleResponse[]>([]);
  // Estado para exibir/capturar brasão
  const [openEmblemDialog, setOpenEmblemDialog] = useState(false);

  // Carrega lista de entidades
  const fetchPublicEntities = async () => {
    try {
      const data = await getAllPublicEntitiesSimple();
      setPublicEntities(data);
    } catch (error) {
      console.error('Erro ao buscar entidades públicas:', error);
      swal('Erro', 'Não foi possível buscar as entidades públicas.', 'error');
    }
  };

  // Se modo edição, carrega config
  const fetchConfigToEdit = async () => {
    if (!cfgId) return;
    setLoading(true);
    try {
      const existing: ConfigPublicacao = await getConfigPublicacao(cfgId);
      setFormData({
        entity_id: existing.id,
        prefix: existing.prefix,
        suffix: existing.suffix,
        type: existing.type,
        manager_name: existing.manager_name,
        editor_name: existing.editor_name,
        address_street: existing.address.street,
        address_cep: existing.address.cep,
        year_roman_start_from: existing.header.year_roman_start_from,
        edition_number_start_from: existing.header.edition_number_start_from,
        header_title: existing.header.title,
        header_subtitle: existing.header.subtitle,
        dom_enabled: existing.dom_enabled,
        dou_enabled: existing.dou_enabled,
        jgc_enabled: existing.jgc_enabled,
        emblem_file: undefined,
      });
    } catch (error) {
      console.error('Erro ao obter config:', error);
      swal('Erro', 'Não foi possível obter a configuração para edição.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicEntities();
    if (isEditMode) {
      fetchConfigToEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgId]);

  // Lidar com inputs de texto e checkboxes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Lidar com a seleção da entidade via react-select
  const handleEntityChange = (selected: any) => {
    setFormData((prev) => ({
      ...prev,
      entity_id: selected ? selected.value : '',
    }));
  };

  // Monta as opções para o select
  const entityOptions = publicEntities.map((ent) => ({
    value: ent.id,
    label: ent.name,
  }));
  // Se já houver entity_id, achar a "option" correspondente
  const selectedEntityOption = entityOptions.find((opt) => opt.value === formData.entity_id) || null;

  // Submeter o form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações mínimas
    if (!formData.entity_id) {
      swal('Erro', 'Selecione a Entidade antes de salvar.', 'error');
      return;
    }
    if (!formData.prefix || !formData.suffix || !formData.type) {
      swal('Erro', 'Prefixo, Sufixo e Tipo são obrigatórios.', 'error');
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && cfgId) {
        await updateConfigPublicacao(cfgId, formData);
        swal('Sucesso', 'Configuração atualizada com sucesso.', 'success');
      } else {
        await createConfigPublicacao(formData);
        swal('Sucesso', 'Configuração criada com sucesso.', 'success');
      }
      navigate('/configuracoes-publicacoes');
    } catch (error) {
      console.error('Erro ao salvar config:', error);
      swal('Erro', 'Não foi possível salvar a configuração.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Se for abrir o dialog de brasão, iremos setar `openEmblemDialog = true`
  // e ao final, iremos receber o `File` (cortado) em `handleEmblemCropped()`.
  const handleEmblemCropped = (croppedFile: File) => {
    // Seta no formData
    setFormData((prev) => ({
      ...prev,
      emblem_file: croppedFile,
    }));
  };

  if (!user?.is_system_admin) {
    return (
      <Typography variant="h6" color="error">
        Você não tem permissão para acessar esta página.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Editar Configuração de Publicação' : 'Nova Configuração de Publicação'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Entidade */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selecione a Entidade (ID vinculado)
              </Typography>
              <Select
                placeholder="Escolha a entidade..."
                options={entityOptions}
                value={selectedEntityOption}
                onChange={handleEntityChange}
                isClearable
              />
            </Grid>
            <Grid item xs={12} sm={6} />

            {/* Prefixo e Sufixo */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prefixo"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sufixo"
                name="suffix"
                value={formData.suffix}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            {/* Tipo */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tipo (Poder Executivo, etc.)"
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            {/* Gestor / Editor */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome do Gestor"
                name="manager_name"
                value={formData.manager_name}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome do Editor"
                name="editor_name"
                value={formData.editor_name}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* Endereço */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Endereço (rua)"
                name="address_street"
                value={formData.address_street}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CEP"
                name="address_cep"
                value={formData.address_cep}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* year_roman_start_from e edition_number_start_from */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ano Romano (início)"
                name="year_roman_start_from"
                type="number"
                value={formData.year_roman_start_from}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Edição (início)"
                name="edition_number_start_from"
                type="number"
                value={formData.edition_number_start_from}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* header_title / header_subtitle */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Título no Cabeçalho"
                name="header_title"
                value={formData.header_title}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subtítulo no Cabeçalho"
                name="header_subtitle"
                value={formData.header_subtitle}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* DOM / DOU / JGC */}
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dom_enabled}
                      onChange={handleChange}
                      name="dom_enabled"
                    />
                  }
                  label="Habilitar DOM"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dou_enabled}
                      onChange={handleChange}
                      name="dou_enabled"
                    />
                  }
                  label="Habilitar DOU"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jgc_enabled}
                      onChange={handleChange}
                      name="jgc_enabled"
                    />
                  }
                  label="Habilitar JGC"
                />
              </FormGroup>
            </Grid>

            {/* Botão de Upload do Brasão */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddPhotoAlternate />}
                onClick={() => setOpenEmblemDialog(true)}
              >
                {formData.emblem_file ? 'Brasão Selecionado!' : 'Selecionar/Cortar Brasão'}
              </Button>
              {formData.emblem_file && (
                <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                  {formData.emblem_file.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/configuracoes-publicacoes')} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Diálogo para recortar e receber o brasão */}
      <EmblemUploadDialog
        open={openEmblemDialog}
        onClose={() => setOpenEmblemDialog(false)}
        onCropComplete={handleEmblemCropped}
      />
    </Box>
  );
};

export default ConfigPubForm;